import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { useMother } from '../lib/MotherContext';
import { TEACHING_CARDS } from '../data/modules';
import { LIBRARY_RESOURCES, MODULE_LIBRARY_MAP } from '../data/library';
import {
  CONCERN_OPTIONS,
  CONCERN_RESULTS,
  computeConcernRisk,
  severityToRisk,
  getAllConcernIds,
  GOAL_CONFIRM,
  getCombinedPlan,
  getContextNote,
  resolveConcernResult,
} from '../data/combinedConcerns';
import RiskBadge from '../components/RiskBadge';
import HelpfulToolsSection from '../components/HelpfulToolsSection';
import { submitSelfCheck } from '../lib/api';
import {
  ArrowRight, MessageCircle, Calendar, Users, ClipboardList, AlertOctagon, BookOpen,
  RotateCcw, Library as LibraryIcon, Play, Image as ImageIcon, FileText, UserPlus,
  Eye, ShieldAlert, Target, ListChecks, Info,
} from 'lucide-react';

const STORAGE_KEY = 'app_selfcheck';

// Legacy module-id -> new concern-id (for backward compatibility on read).
const LEGACY_MODULE_TO_CONCERN = {
  A: 'engorgement', B: 'clog', C: 'mastitis', D: 'nipple_pain', E: 'white_spot',
  F: 'pump_issue', G: 'low_supply', H: 'latch', I: 'work_pump', J: 'wean',
};

const RISK_RANK = { green: 0, yellow: 1, red: 2 };

export default function Result() {
  const { lang, t } = useLanguage();
  const navigate = useNavigate();
  const { mother } = useMother();
  const [submission] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (!submission) { navigate('/self-check'); return; }
    if (submission.id) return; // already submitted in a previous render

    submitSelfCheck({
      idempotency_key: submission.idempotency_key,
      mother_id: submission.mother_id || mother?.id || null,
      language: submission.language,
      user_role: submission.user_role,
      postpartum_age: submission.postpartum_age,
      birth_order: submission.birth_order,
      location_type: submission.location_type,
      location_text: submission.location_text,
      feeding_method: submission.feeding_method,
      main_concern: submission.main_concern,
      primary_concern: submission.primary_concern || submission.main_concern,
      related_concerns: submission.related_concerns || [],
      goal_confirmation: submission.goal_confirmation || null,
      concern_followups: submission.concern_followups || {},
      red_flag_answers: submission.red_flag_answers,
      module_answers: submission.module_answers || {},
      risk_level: submission.risk_level,
      result_type: submission.result_type || 'combined',
      customer_name: submission.customer_name || mother?.name || null,
      customer_phone: submission.customer_phone || mother?.phone || null,
    })
      .then((res) => {
        const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
        stored.id = res.id;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
      })
      .catch(() => {});
  }, [navigate, mother, submission]);

  // Resolve concerns even for legacy submissions where only main_concern/module_id existed.
  const concerns = useMemo(() => {
    if (!submission) return [];
    let ids = getAllConcernIds(submission);
    if (ids.length === 0) {
      const legacy = submission.module_id && LEGACY_MODULE_TO_CONCERN[submission.module_id];
      if (legacy) ids = [legacy];
    }
    return ids.filter((id) => !!CONCERN_RESULTS[id]);
  }, [submission]);

  // Compute per-concern severity → risk badge.
  const concernRisks = useMemo(() => {
    if (!submission) return {};
    const out = {};
    const fu = submission.concern_followups || {};
    for (const cid of concerns) {
      out[cid] = severityToRisk(computeConcernRisk(cid, fu[cid] || {}));
    }
    return out;
  }, [submission, concerns]);

  // Headline: highest-risk concern title, fallback to first concern's title.
  const headlineConcern = useMemo(() => {
    if (concerns.length === 0) return null;
    return [...concerns].sort(
      (a, b) => (RISK_RANK[concernRisks[b]] || 0) - (RISK_RANK[concernRisks[a]] || 0),
    )[0];
  }, [concerns, concernRisks]);

  // Aggregate "Related teaching" across all concerns.
  const teachingIds = useMemo(() => {
    const set = new Set();
    if (concerns.includes('engorgement') || concerns.includes('clog') || concerns.includes('low_supply') || concerns.includes('white_spot')) set.add('hand_expression');
    if (concerns.includes('white_spot') || concerns.includes('engorgement') || concerns.includes('clog')) set.add('nipple_massage');
    if (concerns.includes('white_spot')) set.add('epsom_salt');
    if (concerns.includes('latch') || concerns.includes('nipple_pain')) set.add('latch_positions');
    // Flange-fit applies to pump-related concerns, OR to nipple_pain when
    // latch is NOT also selected (so we don't imply flange size fixes latch).
    if (concerns.includes('pump_issue')) set.add('flange_fit');
    if (concerns.includes('nipple_pain') && !concerns.includes('latch')) set.add('flange_fit');
    return Array.from(set);
  }, [concerns]);

  // Related library across concerns (dedup).
  const relatedLibrary = useMemo(() => {
    const ids = new Set();
    for (const cid of concerns) {
      const moduleId = Object.keys(LEGACY_MODULE_TO_CONCERN).find((k) => LEGACY_MODULE_TO_CONCERN[k] === cid);
      if (moduleId && MODULE_LIBRARY_MAP[moduleId]) {
        for (const rid of MODULE_LIBRARY_MAP[moduleId]) ids.add(rid);
      }
    }
    return Array.from(ids);
  }, [concerns]);

  if (!submission) {
    return <div className="container-app py-10">{t('loading')}</div>;
  }

  const overallRisk = submission.risk_level || 'green';

  const headlineTitle = headlineConcern
    ? (() => {
        const data = resolveConcernResult(headlineConcern, submission) || CONCERN_RESULTS[headlineConcern];
        return lang === 'zh' ? data.title_zh : data.title_en;
      })()
    : (lang === 'zh' ? '感谢你的资料，我们将尽快联系' : 'Thanks for your details — we will reach out soon');

  const restartCheck = () => { localStorage.removeItem(STORAGE_KEY); navigate('/self-check'); };

  const goalConfirmText = (() => {
    if (!submission.goal_confirmation) return null;
    const opt = GOAL_CONFIRM.options.find((o) => o.id === submission.goal_confirmation);
    if (!opt) return null;
    return lang === 'zh' ? opt.zh : opt.en;
  })();

  return (
    <div className="container-app py-8 sm:py-12 max-w-3xl animate-fade-in-up" data-testid="result-page">
      {/* Risk header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <span className="text-xs uppercase tracking-[0.2em] font-semibold text-terracotta">
          {t('result_likely')}
        </span>
        <RiskBadge level={overallRisk} size="lg" />
      </div>

      <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-ink leading-snug mb-3" data-testid="result-headline">
        {headlineTitle}
      </h1>

      {/* Concern chips */}
      {concerns.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6" data-testid="concern-chips">
          {concerns.map((cid, i) => {
            const opt = CONCERN_OPTIONS.find((o) => o.id === cid);
            const label = opt ? (lang === 'zh' ? opt.zh : opt.en) : cid;
            const isPrimary = i === 0;
            return (
              <span
                key={cid}
                data-testid={`chip-${cid}`}
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium ${isPrimary ? 'bg-terracotta text-white' : 'bg-terracotta-light text-terracotta'}`}
              >
                {isPrimary && <span className="text-[10px] uppercase tracking-wider opacity-80">{t('result_primary_chip')}</span>}
                {label}
              </span>
            );
          })}
        </div>
      )}

      {/* Goal confirmation note */}
      {goalConfirmText && (
        <div className="mb-6 p-4 rounded-2xl bg-sage-light/40 border border-sage/20 flex items-start gap-3" data-testid="goal-confirm-banner">
          <Target size={18} className="text-sage flex-shrink-0 mt-0.5" />
          <div className="text-sm text-ink-secondary leading-relaxed">
            <span className="font-semibold text-ink">{t('result_goal_label')}: </span>{goalConfirmText}
          </div>
        </div>
      )}

      {/* Guest reminder banner */}
      {!mother && (
        <div data-testid="guest-reminder-banner" className="mb-6 p-4 rounded-2xl bg-terracotta-light/40 border border-terracotta/20 flex gap-3 items-start">
          <UserPlus size={18} className="text-terracotta flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm text-ink-secondary leading-relaxed">
            <div className="mb-2">{t('selfcheck_guest_reminder')}</div>
            <Link to="/profile/start?next=/result" data-testid="guest-reminder-create-btn" className="inline-flex items-center gap-1 text-terracotta font-medium hover:underline">
              {t('selfcheck_gate_create')} <ArrowRight size={12} />
            </Link>
          </div>
        </div>
      )}

      {/* Red banner — only when truly red */}
      {overallRisk === 'red' && (
        <div data-testid="red-warning-banner" className="mb-6 p-5 bg-risk-redbg border-2 border-risk-red/30 rounded-2xl flex gap-3">
          <AlertOctagon size={20} className="text-risk-red flex-shrink-0 mt-0.5" />
          <div className="text-sm text-risk-red leading-relaxed">
            {lang === 'zh'
              ? '你的回答显示有需要立即注意的症状。请尽快联系医生评估，并继续保持哺乳支援。'
              : 'Your answers show symptoms that need urgent attention. Please contact a doctor as soon as possible and continue lactation support.'}
          </div>
        </div>
      )}

      {/* Combined Summary + Priority Plan (only when 2+ concerns OR a specific
          plan exists). Single-concern users skip this and go straight to the
          primary card to avoid repeating the same advice twice. */}
      {(() => {
        const plan = getCombinedPlan(concerns, submission.goal_confirmation);
        if (!plan && concerns.length < 2) return null;
        // Fallback when no specific combo is mapped: use a generic stitched plan
        const summary = plan
          ? (lang === 'zh' ? plan.summary_zh : plan.summary_en)
          : (lang === 'zh'
            ? '你选择了多个相关的问题。下方每张卡片会分别说明每个问题的处理方式；先从主要问题开始处理。'
            : 'You picked several related concerns. Each card below covers one concern — start with the primary card first.');
        const priority = plan
          ? (lang === 'zh' ? plan.priority_zh : plan.priority_en)
          : null;
        return (
          <section
            className="mb-8 rounded-3xl bg-paper border border-terracotta/30 p-6 sm:p-8"
            data-testid="combined-summary-section"
          >
            <div className="flex items-center gap-2 mb-3">
              <ListChecks size={18} className="text-terracotta" />
              <h2 className="font-heading text-lg font-semibold text-ink">
                {t('result_combined_summary_title')}
              </h2>
            </div>
            <p className="text-sm text-ink-secondary leading-relaxed mb-5" data-testid="combined-summary-text">
              {summary}
            </p>
            {priority && priority.length > 0 && (
              <>
                <h3 className="text-xs uppercase tracking-[0.18em] font-semibold text-terracotta mb-3">
                  {t('result_priority_plan')}
                </h3>
                <ol className="space-y-2" data-testid="combined-priority-list">
                  {priority.map((p, i) => (
                    <li key={i} className="flex gap-3 text-sm text-ink-secondary leading-relaxed">
                      <span className="w-6 h-6 rounded-full bg-terracotta text-white flex items-center justify-center text-[11px] font-semibold flex-shrink-0 mt-0.5">{i + 1}</span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ol>
              </>
            )}
          </section>
        );
      })()}

      {/* Per-concern blocks */}
      {concerns.map((cid, idx) => {
        const data = resolveConcernResult(cid, submission) || CONCERN_RESULTS[cid];
        if (!data) return null;
        const opt = CONCERN_OPTIONS.find((o) => o.id === cid);
        const label = opt ? (lang === 'zh' ? opt.zh : opt.en) : cid;
        const why = lang === 'zh' ? data.why_zh : data.why_en;
        const steps = lang === 'zh' ? data.what_to_do_zh : data.what_to_do_en;
        const observe = (lang === 'zh' ? data.observe_zh : data.observe_en) || null;
        const whenSeek = (lang === 'zh' ? data.when_to_seek_zh : data.when_to_seek_en) || null;
        const extra = (lang === 'zh' ? data.extra_zh : data.extra_en) || null;
        const sectionRisk = concernRisks[cid];
        const otherIds = concerns.filter((c) => c !== cid);
        const ctxNote = getContextNote(cid, otherIds, lang);

        return (
          <section
            key={cid}
            data-testid={`concern-block-${cid}`}
            className="mb-8 border border-border rounded-3xl overflow-hidden"
          >
            <div className="px-6 sm:px-8 py-5 bg-terracotta-light/30 border-b border-border flex items-center justify-between gap-3">
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-terracotta mb-1">
                  {idx === 0 ? t('result_primary_chip') : t('result_related_chip')} · {label}
                </div>
                <h2 className="font-heading text-xl font-semibold text-ink leading-snug">
                  {lang === 'zh' ? data.title_zh : data.title_en}
                </h2>
              </div>
              <RiskBadge level={sectionRisk} size="sm" />
            </div>

            <div className="p-6 sm:p-8 space-y-6">
              {/* Context note: e.g. "you also picked latch — see latch card" */}
              {ctxNote && (
                <div
                  data-testid={`context-note-${cid}`}
                  className="flex gap-3 p-3 rounded-2xl bg-sage-light/40 border border-sage/20"
                >
                  <Info size={16} className="text-sage flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-ink-secondary leading-relaxed">{ctxNote}</p>
                </div>
              )}

              {/* Why */}
              <div>
                <h3 className="font-heading text-base font-semibold text-ink mb-2 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-terracotta-light flex items-center justify-center text-terracotta text-[10px] font-bold">1</span>
                  {t('result_why')}
                </h3>
                <p className="text-sm text-ink-secondary leading-relaxed">{why}</p>
              </div>

              {/* What to do */}
              <div>
                <h3 className="font-heading text-base font-semibold text-ink mb-2 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-terracotta-light flex items-center justify-center text-terracotta text-[10px] font-bold">2</span>
                  {t('result_what_to_do')}
                </h3>
                <ul className="space-y-2">
                  {steps.map((s, i) => (
                    <li key={i} className="flex gap-2 text-sm text-ink-secondary leading-relaxed">
                      <span className="w-5 h-5 rounded-full bg-sage-light text-sage flex items-center justify-center text-[10px] font-semibold flex-shrink-0 mt-0.5">{i + 1}</span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Extra structured sections */}
              {extra && extra.length > 0 && (
                <div className="space-y-4">
                  {extra.map((sec, i) => (
                    <div key={i}>
                      <h4 className="font-heading text-sm font-semibold text-ink mb-2">{sec.title}</h4>
                      <ul className="space-y-1.5">
                        {sec.items.map((it, j) => (
                          <li key={j} className="text-sm text-ink-secondary leading-relaxed flex gap-2">
                            <span className="text-sage mt-1.5">•</span><span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}

              {/* Observe */}
              {observe && observe.length > 0 && (
                <div className="bg-sage-light/30 p-4 rounded-2xl">
                  <h3 className="font-heading text-base font-semibold text-ink mb-2 flex items-center gap-2">
                    <Eye size={16} className="text-sage" />{t('result_observe')}
                  </h3>
                  <ul className="space-y-1.5">
                    {observe.map((o, i) => (
                      <li key={i} className="text-sm text-ink-secondary leading-relaxed flex gap-2">
                        <span className="text-sage mt-1.5">•</span><span>{o}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* When to seek medical — only show on the primary card to avoid
                  duplicating the same warning across cards. */}
              {idx === 0 && whenSeek && whenSeek.length > 0 && (
                <div className="border-l-4 border-ink-tertiary/30 pl-4" data-testid={`when-seek-${cid}`}>
                  <h3 className="font-heading text-sm font-semibold text-ink mb-2 flex items-center gap-2">
                    <ShieldAlert size={14} className="text-ink-secondary" />{t('result_when_seek_medical')}
                  </h3>
                  <ul className="space-y-1.5">
                    {whenSeek.map((w, i) => (
                      <li key={i} className="text-sm text-ink-secondary leading-relaxed flex gap-2">
                        <span className="text-ink-tertiary mt-1.5">•</span><span>{w}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </section>
        );
      })}

      {/* Teaching resources (aggregated) */}
      {teachingIds.length > 0 && (
        <section className="mb-6">
          <h2 className="font-heading text-lg font-semibold text-ink mb-4 flex items-center gap-2">
            <BookOpen size={18} className="text-sage" />{t('result_teaching')}
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {teachingIds.map((tid) => {
              const tc = TEACHING_CARDS[tid];
              if (!tc) return null;
              return (
                <Link to={`/teaching/${tid}`} key={tid} data-testid={`teaching-card-${tid}`} className="card-interactive overflow-hidden group">
                  {tc.image_url ? (
                    <div className="h-32 bg-cover bg-center" style={{ backgroundImage: `url(${tc.image_url})` }} />
                  ) : (
                    <div className="h-32 bg-gradient-to-br from-sage-light/60 via-paper to-terracotta-light/50 flex items-center justify-center">
                      <BookOpen size={28} className="text-sage" />
                    </div>
                  )}
                  <div className="p-5">
                    <h3 className="font-heading font-semibold text-ink mb-1 group-hover:text-terracotta transition-colors">
                      {lang === 'zh' ? tc.title_zh : tc.title_en}
                    </h3>
                    <p className="text-xs text-ink-tertiary">
                      {(lang === 'zh' ? tc.use_for_zh : tc.use_for_en).slice(0, 2).join(' · ')}
                    </p>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Related library */}
      {relatedLibrary.length > 0 && (
        <section className="mb-6" data-testid="related-library-section">
          <h2 className="font-heading text-lg font-semibold text-ink mb-4 flex items-center gap-2">
            <LibraryIcon size={18} className="text-terracotta" />{t('result_related_library')}
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {relatedLibrary.map((rid) => {
              const r = LIBRARY_RESOURCES.find((x) => x.id === rid);
              if (!r) return null;
              const Icon = r.type === 'video' ? Play : r.type === 'image_guide' ? ImageIcon : FileText;
              return (
                <Link key={rid} to="/library" data-testid={`related-lib-${rid}`} className="card-interactive p-4 flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-terracotta-light/40 flex items-center justify-center flex-shrink-0">
                    <Icon size={16} className="text-terracotta" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-heading text-sm font-semibold text-ink leading-snug line-clamp-2">
                      {lang === 'zh' ? r.title_zh : r.title_en}
                    </h3>
                    {r.duration && <span className="text-[10px] text-ink-tertiary">{r.duration}</span>}
                  </div>
                  <ArrowRight size={14} className="ml-auto text-ink-tertiary flex-shrink-0" />
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Disclaimer */}
      <p className="text-xs text-ink-tertiary text-center my-6 leading-relaxed">{t('result_disclaimer')}</p>

      {/* Helpful tools (Phase 2 — partner products, eligibility-gated) */}
      <HelpfulToolsSection submission={submission} />

      {/* CTAs */}
      <div className="grid sm:grid-cols-2 gap-3">
        <Link to="/whatsapp-summary" data-testid="cta-whatsapp" className="btn-primary w-full">
          <MessageCircle size={18} className="mr-2" /> {t('result_send_whatsapp')}
        </Link>
        <Link to="/booking" data-testid="cta-booking" className="btn-secondary w-full">
          <Calendar size={18} className="mr-2" /> {t('result_book')}
        </Link>
        <Link to="/consultants" data-testid="cta-consultants" className="btn-outline w-full">
          <Users size={18} className="mr-2" /> {t('result_choose_consultant')}
        </Link>
        <Link to="/milk-log" data-testid="cta-milk-log" className="btn-outline w-full">
          <ClipboardList size={18} className="mr-2" /> {t('result_start_log')}
        </Link>
      </div>

      <button onClick={restartCheck} data-testid="restart-btn" className="mt-8 w-full text-sm text-ink-tertiary hover:text-terracotta inline-flex items-center justify-center gap-2">
        <RotateCcw size={14} /> {t('start_over')}
      </button>
    </div>
  );
}
